<template>
  <div class="product-management-wrapper">
    <div v-if="isDefaultDescriptionEditorVisible"><ResourceManagement ref="templateManager" resource-type="template" /><button type="button" @click="handleCloseDefaultDescriptionEditor">返回商品列表</button></div>
    <ProductForm
      v-else-if="isFormVisible"
      :product-id="editingProductId"
      @form-saved="handleFormSaved"
      @form-cancelled="handleFormCancelled"
  />
    <div v-else class="product-list-view"> <div class="page-breadcrumb">
      <span>內容管理</span> <i class="fas fa-chevron-right separator"></i> <span>商品管理</span>
    </div>

      <div class="filter-bar filter-bar-responsive">
        <input type="text" v-model="searchName" data-testid="resource-search" placeholder="依商品名稱搜尋..." class="form-input search-input" @keyup.enter="applyFiltersAndResetPage"/>
        <select v-model="searchCategoryName" class="form-input search-select" @change="applyFiltersAndResetPage">
          <option value="">所有分類</option>
          <option v-for="cat in availableCategoriesForFilter" :key="cat.id" :value="cat.name"> {{ cat.name }}
          </option>
        </select>
        <select v-model="searchHot" class="form-input search-select boolean-select" @change="applyFiltersAndResetPage">
          <option :value="null">熱門(全部)</option>
          <option :value="true">是</option>
          <option :value="false">否</option>
        </select>
        <select v-model="searchFeatured" class="form-input search-select boolean-select" @change="applyFiltersAndResetPage">
          <option :value="null">精選(全部)</option>
          <option :value="true">是</option>
          <option :value="false">否</option>
        </select>
        <button class="filter-btn" data-testid="resource-search-submit" @click="applyFiltersAndResetPage">
          <i class="fas fa-search"></i> 搜尋
        </button>
        <button class="clear-filter-btn" @click="clearFiltersAndFetch">
          <i class="fas fa-times"></i> 清除
        </button>
      </div>

      <div class="actions-bar">
        <button class="add-product-btn" data-testid="resource-create" @click="openProductForm()">
          <i class="fas fa-plus-circle"></i> 新增商品
        </button>
        <button class="edit-default-desc-btn" @click="handleOpenDefaultDescriptionEditor">
          <i class="fas fa-file-alt"></i> 編輯預設描述
        </button>
      </div>

      <div v-if="isLoading" class="loading-spinner">
        <p>載入商品中...</p>
      </div>

      <div v-if="!isLoading && products.length > 0" class="product-grid">
        <div v-for="product in products" :key="product.id" :data-resource-id="product.id" class="product-card">
          <div class="product-image-container">
            <img v-if="product.imageUrl" :src="product.imageUrl" :alt="product.name" class="product-image">
            <div v-else class="product-image-placeholder-card">
              <span>{{ product.name }} - 無圖片</span>
            </div>
            <div class="product-badges">
              <span v-if="product.featured" class="badge featured-badge" title="精選遊戲">精選</span>
              <span v-if="product.hot" class="badge hot-badge" title="熱門遊戲">熱門</span>
            </div>
          </div>
          <div class="product-info">
            <h3 class="product-name">{{ product.name }}</h3>
            <p class="product-category">分類: {{ product.categoryName }}</p>
            <div class="product-status-flags">
              <span v-if="product.featured" class="status-flag featured-text"><i class="fas fa-star"></i> 精選</span>
              <span v-if="product.hot" class="status-flag hot-text"><i class="fas fa-fire"></i> 熱門</span>
            </div>
          </div>
          <div class="product-actions">
            <button class="action-link edit-btn" data-testid="resource-edit" @click="openProductForm(product.id)">
              <i class="fas fa-edit"></i> 編輯
            </button>
            <button class="action-link delete-btn" data-testid="resource-delete" @click="confirmDeleteProduct(product.id, product.name)">
              <i class="fas fa-trash-alt"></i> 刪除
            </button>
          </div>
        </div>
      </div>

      <div v-if="!isLoading && products.length === 0" class="no-data-message">
        目前沒有符合條件的商品資料。
      </div>

      <div class="pagination-controls" v-if="!isLoading && totalItems > 0">
        <div class="items-per-page-selector">
          <label for="itemsPerPageSelect">每頁顯示：</label>
          <select id="itemsPerPageSelect" v-model.number="itemsPerPage" @change="handleItemsPerPageChange" class="select-dropdown">
            <option v-for="option in perPageOptions" :key="option" :value="option">
              {{ option }}
            </option>
          </select>
          <span class="total-items-info"> (共 {{ totalItems }} 項)</span>
        </div>
        <div class="page-navigation">
          <button @click="goToPage(1)" :disabled="currentPage === 1" class="page-btn">
            <i class="fas fa-angle-double-left"></i>
          </button>
          <button @click="prevPage" :disabled="currentPage === 1" class="page-btn">
            <i class="fas fa-angle-left"></i>
          </button>
          <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 頁</span>
          <button @click="nextPage" :disabled="currentPage === totalPages || totalPages === 0" class="page-btn">
            <i class="fas fa-angle-right"></i>
          </button>
          <button @click="goToPage(totalPages)" :disabled="currentPage === totalPages || totalPages === 0" class="page-btn">
            <i class="fas fa-angle-double-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import Swal from '@/utils/chineseSwal';
import ProductForm from '@/components/ProductForm.vue';
import ResourceManagement from '@/components/ResourceManagement.vue'; // 導入新的編輯器組件
import {
  deleteProductById,
  queryProducts
} from '@/api/product.api.js';
import { getAllCategories } from '@/api/category.api.js';

const toast = useToast();

const products = ref([]);
const isLoading = ref(true);
const availableCategoriesForFilter = ref([]); // 用於篩選器的分類列表
const currentPage = ref(1);
const itemsPerPage = ref(10);
const perPageOptions = ref([10, 20, 30, 100]);
const totalPages = ref(0);
const totalItems = ref(0);
const searchName = ref('');
const searchCategoryName = ref('');
const searchHot = ref(null);
const searchFeatured = ref(null);

// 控制表單顯示的狀態
const isFormVisible = ref(false);
const editingProductId = ref(null); // null 表示新增，有值表示編輯
const templateManager = ref(null);
const isDefaultDescriptionEditorVisible = ref(false); // state 控制預設描述編輯器的顯示

const fetchAvailableCategoriesForFilter = async () => {
  try {
    const data = await getAllCategories();
    availableCategoriesForFilter.value = data;
  } catch (error) {
    console.error("獲取分類選項失敗 (列表):", error);
    toast.error('無法載入商品分類選項。');
  }
};

const fetchProducts = async () => {
  isLoading.value = true;
  const queryParams = {
    page: currentPage.value - 1,
    size: itemsPerPage.value,
    name: searchName.value.trim() || undefined,
    categoryName: searchCategoryName.value || undefined,
    featured: searchFeatured.value === null ? undefined : searchFeatured.value,
    hot: searchHot.value === null ? undefined : searchHot.value,
  };
  Object.keys(queryParams).forEach(key => {
    if (queryParams[key] === undefined) {
      delete queryParams[key];
    }
  });

  try {
    const response = await queryProducts(queryParams);
    products.value = response.content.map(p => ({
      ...p,
      imageUrl: p.imageUrl || ''
    }));
    totalItems.value = response.totalElements;
    totalPages.value = response.totalPages;
    if (products.value.length === 0 && totalItems.value > 0 && currentPage.value > 1) {
      currentPage.value = totalPages.value > 0 ? totalPages.value : 1;
    } else if (totalPages.value > 0 && currentPage.value > totalPages.value) {
      currentPage.value = totalPages.value;
    } else if (totalPages.value === 0 && products.value.length === 0 ) {
      currentPage.value = 1;
    }
  } catch (error) {
    console.error("獲取商品失敗:", error);
    toast.error('獲取商品資料失敗，請稍後再試。');
    products.value = [];
    totalItems.value = 0;
    totalPages.value = 0;
  } finally {
    isLoading.value = false;
  }
};

const handleOpenDefaultDescriptionEditor = () => {
  isFormVisible.value = false; // 如果商品表單是打開的，先關閉它
  editingProductId.value = null; // 清除可能存在的商品編輯 ID
  isDefaultDescriptionEditorVisible.value = true;
};

const handleCloseDefaultDescriptionEditor = () => {
  if (templateManager.value && !templateManager.value.confirmDiscard()) return;
  isDefaultDescriptionEditorVisible.value = false;
  // 關閉後，用戶回到商品列表視圖
};

onMounted(() => {
  fetchAvailableCategoriesForFilter();
  fetchProducts();
});

watch([currentPage, itemsPerPage], () => {
  if (!isFormVisible.value) { // 只有在列表視圖時才因分頁變化刷新
    fetchProducts();
  }
});

const applyFiltersAndResetPage = () => {
  if (currentPage.value === 1) {
    fetchProducts();
  } else {
    currentPage.value = 1; // 會觸發 watch
  }
};
const clearFiltersAndFetch = () => {
  searchName.value = '';
  searchCategoryName.value = '';
  searchHot.value = null;
  searchFeatured.value = null;
  applyFiltersAndResetPage();
};

const openProductForm = (productId = null) => {
  editingProductId.value = productId;
  isFormVisible.value = true;
};

const handleFormSaved = async () => {
  isFormVisible.value = false;
  editingProductId.value = null;
  await fetchProducts(); // 刷新列表
};

const handleFormCancelled = () => {
  isFormVisible.value = false;
  editingProductId.value = null;
  fetchProducts();
};

const confirmDeleteProduct = async (id, name) => {
  Swal.fire({
    title: '確定要刪除嗎?',
    html: `您確定要刪除商品 "<strong>${name}</strong>" 嗎？<br>此操作無法復原！`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: '是的，刪除它！',
    cancelButtonText: '取消',
    customClass: {
      popup: 'swal2-dark-popup',
      title: 'swal2-dark-title',
      htmlContainer: 'swal2-dark-html-container',
      confirmButton: 'swal2-dark-confirm-button',
      cancelButton: 'swal2-dark-cancel-button'
    },
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await deleteProductById(id);
        toast.success(`商品 "${name}" 已成功刪除！`);
        // 刪除成功後，如果當前頁變為空且不是第一頁，則跳轉到前一頁
        if (products.value.length === 1 && currentPage.value > 1) {
          currentPage.value--; // 這會觸發 watch
        } else {
          await fetchProducts(); // 否則重新獲取當前頁
        }
      } catch (error) {
        console.error("刪除商品失敗:", error);
        const errorMessage = error.response?.data?.message || error.message || '操作失敗，請稍後再試。';
        toast.error(`刪除商品失敗：${errorMessage}`);
      }
    }
  });
};

// goToPage, nextPage, prevPage, handleItemsPerPageChange 保持不變
const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value && page !== currentPage.value) {
    currentPage.value = page;
  }
};
const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};
const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};
const handleItemsPerPageChange = () => {
  if (currentPage.value === 1) {
    fetchProducts();
  } else {
    currentPage.value = 1;
  }
};
</script>
<style>
@media (max-width: 768px) {
  .content-body {
    padding: 20px 16px!important;
  }
}
</style>

<style scoped>
/* ProductsView.vue 的樣式 */
.product-management-wrapper { /* 父組件的頂層 class */
  padding: 20px; /* 可以保留父組件的 padding */
  color: #c0c0c0; /* 或者將此類通用樣式移至全局 */
}

.product-list-view { /* 列表部分的容器 */
  /* 如果 ProductForm 有自己的背景和 padding，這裡可能不需要額外的 */
}

/* Filter Bar Styles */
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 25px;
  align-items: center;
  background-color: rgba(30, 30, 40, 0.7);
  padding: 15px;
  border-radius: 8px;
  border: 1px solid rgba(160, 100, 220, 0.3);
}

.form-input {
  padding: 10px 12px;
  background-color: rgba(20, 20, 30, 0.8);
  border: 1px solid rgba(160, 100, 220, 0.4);
  border-radius: 5px;
  color: #e0e0e0;
  font-size: 1em;
  box-sizing: border-box;
  height: 38px;
}
.form-input:focus {
  outline: none;
  border-color: #ff8c69;
  box-shadow: 0 0 8px rgba(255, 140, 105, 0.3);
}

.form-input.search-input {
  flex: 1 1 200px;
}
.form-input.search-select {
  flex: 1 1 150px;
  min-width: 120px;
  padding-top: 0;
  padding-bottom: 0;
  height: 38px;
}
.form-input.search-select.boolean-select {
  flex: 0 1 140px;
  min-width: 130px;
}


.filter-btn, .clear-filter-btn {
  padding: 0 15px;
  height: 38px;
  line-height: 36px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9em;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  min-width: 90px;
  flex-shrink: 0;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.filter-btn i, .clear-filter-btn i { margin-right: 6px; }

.filter-btn {
  background: linear-gradient(145deg, #8be9fd, #50c8ea);
  color: #1a1a1a;
  border-color: #50c8ea;
}
.filter-btn:hover {
  background: linear-gradient(145deg, #a0eefe, #60d0f0);
  box-shadow: 0 2px 8px rgba(139, 233, 253, 0.2);
}

.clear-filter-btn {
  background-color: rgba(80, 80, 90, 0.7);
  color: #c0c0c0;
  border-color: rgba(100,100,110,0.8);
}
.clear-filter-btn:hover {
  background-color: rgba(90, 90, 100, 0.8);
}

.actions-bar {
  margin-bottom: 25px;
  display: flex;
  justify-content: flex-start;
}

.add-product-btn {
  background: linear-gradient(145deg, #50fa7b, #3ddc67);
  color: #1a1a1a;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 0.95em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 2px 8px rgba(80, 250, 123, 0.2);
}
.add-product-btn i { margin-right: 8px; }
.add-product-btn:hover {
  background: linear-gradient(145deg, #60fc8b, #4dfd77);
  box-shadow: 0 4px 12px rgba(80, 250, 123, 0.3);
  transform: translateY(-1px);
}

.loading-spinner, .no-data-message {
  text-align: center;
  padding: 40px;
  font-size: 1.1em;
  color: #888e95;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 25px;
  margin-bottom: 30px;
}

.product-card {
  background-color: rgba(35, 35, 45, 0.7);
  border: 1px solid rgba(160, 100, 220, 0.3);
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 15px rgba(0,0,0,0.3);
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}
.product-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(160, 100, 220, 0.2);
}

.product-image-container {
  width: 100%;
  height: 180px;
  background-color: rgba(50, 50, 60, 0.8);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  overflow: hidden;
  position: relative;
}

.product-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
  display: block;
}

.product-image-placeholder-card {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #888e95;
  font-size: 0.9em;
  text-align: center;
  padding: 10px;
  box-sizing: border-box;
}

.product-badges {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: flex-end;
}
.badge {
  padding: 3px 7px;
  border-radius: 4px;
  font-size: 0.75em;
  font-weight: bold;
  color: #fff;
  text-shadow: 0 0 3px rgba(0,0,0,0.5);
  display: inline-block;
}
.featured-badge {
  background-color: rgba(255, 140, 105, 0.85);
}
.hot-badge {
  background-color: rgba(255, 85, 85, 0.85);
}
.product-info { margin-bottom: 15px; flex-grow: 1; }
.product-name {
  font-size: 1.2em;
  color: #e0e0e0;
  margin-top: 0;
  margin-bottom: 8px;
  font-weight: 600;
  word-break: break-all;
}
.product-description {
  font-size: 0.85em;
  color: #a0a0b0;
  margin-bottom: 8px;
  height: 3.6em;
  line-height: 1.2em;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}
.product-category { font-size: 0.9em; color: #a0a0b0; margin-bottom: 5px; }
.product-status-flags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 8px;
  font-size: 0.85em;
}
.status-flag i {
  margin-right: 4px;
}
.featured-text {
  color: #ff8c69;
}
.hot-text {
  color: #ff5555;
}

.edit-default-desc-btn {
  background-color: rgba(100, 100, 120, 0.7); /* 一個不同的中性色 */
  color: #e0e0e0;
  border: 1px solid rgba(120,120,140,0.8);
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 0.95em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  margin-left: 15px; /* 與新增商品按鈕隔開 */
}
.edit-default-desc-btn i { margin-right: 8px; }
.edit-default-desc-btn:hover {
  background-color: rgba(110, 110, 130, 0.8);
  box-shadow: 0 2px 8px rgba(100, 100, 120, 0.3);
  transform: translateY(-1px);
}
.product-actions {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  border-top: 1px solid rgba(160, 100, 220, 0.2);
  padding-top: 15px;
}
.action-link {
  background: none;
  border: 1px solid rgba(160, 100, 220, 0.4);
  color: #adb5bd;
  padding: 6px 0;
  text-align: center;
  border-radius: 4px;
  text-decoration: none;
  font-size: 0.85em;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
}
.action-link i { margin-right: 4px; }
.action-link.edit-btn { border-color: rgba(139, 233, 253, 0.5); color: #8be9fd; }
.action-link.edit-btn:hover { background-color: rgba(139, 233, 253, 0.15); color: #a0eefe; }
.action-link.delete-btn { border-color: rgba(255, 85, 85, 0.5); color: #ff8080; }
.action-link.delete-btn:hover { background-color: rgba(255, 85, 85, 0.15); color: #ff6666; }

.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  border-top: 1px solid rgba(160, 100, 220, 0.2);
  color: #a0a0b0;
  font-size: 0.9em;
}
.items-per-page-selector { display: flex; align-items: center; gap: 8px; }
.select-dropdown {
  background-color: rgba(40, 40, 50, 0.9);
  color: #e0e0e0;
  border: 1px solid rgba(160, 100, 220, 0.5);
  padding: 6px 10px;
  border-radius: 5px;
  font-size: 0.95em;
}
.select-dropdown:focus {
  outline: none;
  border-color: #ff8c69;
  box-shadow: 0 0 5px rgba(255, 140, 105, 0.3);
}
.total-items-info { color: #888e95; font-size: 0.9em; }
.page-navigation { display: flex; align-items: center; gap: 8px; }
.page-btn {
  background-color: rgba(60, 60, 75, 0.7);
  border: 1px solid rgba(160, 100, 220, 0.4);
  color: #adb5bd;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  font-size: 0.9em;
}
.page-btn:hover:not(:disabled) {
  background-color: rgba(160, 100, 220, 0.2);
  color: #f8f9fa;
  border-color: rgba(160, 100, 220, 0.6);
}
.page-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.page-info { padding: 0 10px; color: #e0e0e0; font-weight: 500; }

:global(.swal2-dark-popup) {
  background-color: rgba(35, 35, 45, 0.98) !important;
  border: 1px solid rgba(160, 100, 220, 0.6) !important;
  color: #e0e0e0 !important;
}
:global(.swal2-dark-title) {
  color: #ff8c69 !important;
}
:global(.swal2-dark-html-container) {
  color: #c0c0c0 !important;
}
:global(.swal2-dark-confirm-button) {
  background-color: #d33 !important;
  border-color: #d33 !important;
  color: #fff !important;
}
:global(.swal2-dark-confirm-button:hover) {
  background-color: #c82333 !important;
  border-color: #bd2130 !important;
}
:global(.swal2-dark-cancel-button) {
  background-color: rgba(80, 80, 90, 0.7) !important;
  color: #c0c0c0 !important;
}
:global(.swal2-dark-cancel-button:hover) {
  background-color: rgba(90, 90, 100, 0.8) !important;
}

/* RWD 樣式 */
@media (max-width: 1280px) {
  .filter-bar.filter-bar-responsive { /* 確保 class 正確應用 */
    gap: 10px; /* 減少間距 */
  }

  .filter-bar.filter-bar-responsive > * {
    flex: 1 1 calc(33.333% - 10px); /* 嘗試每行三個，如果空間允許 */
  }
  .filter-bar.filter-bar-responsive .search-input {
    flex: 1 1 calc(50% - 10px); /* 名稱搜尋可以寬一點 */
  }

  .filter-bar.filter-bar-responsive .filter-btn,
  .filter-bar.filter-bar-responsive .clear-filter-btn {
    flex: 0 1 auto; /* 按鈕不要彈性增長，保持其 min-width */
    min-width: 90px;
  }
}

@media (max-width: 1024px) {
  .filter-bar.filter-bar-responsive > * {
    flex: 1 1 calc(50% - 10px); /* 平板，每行兩個 */
  }
  .filter-bar.filter-bar-responsive .search-input {
    flex-basis: 100%; /* 名稱搜尋單獨一行 */
    margin-bottom: 10px; /* 與下方元素隔開 */
  }
}

@media (max-width: 768px) {
  .filter-bar.filter-bar-responsive {
    flex-direction: column;
    align-items: stretch;
  }
  .filter-bar.filter-bar-responsive > * {
    width: 100%;
    flex-basis: auto !important; /* 取消 flex-basis */
    margin-bottom: 10px;
  }
  .filter-bar.filter-bar-responsive > *:last-child {
    margin-bottom: 0;
  }

  .filter-bar.filter-bar-responsive .filter-btn,
  .filter-bar.filter-bar-responsive .clear-filter-btn {
    width: 100%;
    padding: 10px 12px;
    font-size: 0.95em;
    min-width: unset;
  }
}
</style>
